/**
 * @file        src/pages/insurance/BookingList.jsx
 * @description Travel-insurance policy list over `insurance_bookings` (the
 *              standalone policies bought from the storefront's Insurance
 *              flow). Status summary cards that double as filters, cover /
 *              plan / policy columns, and per-row Details + Tax Invoice.
 *
 *              ONE component serves both sections: CorporateBookingList.jsx
 *              renders it with `audience="b2b"`. B2C Insurance and Corporate
 *              Insurance are SEPARATE SCREENS, not two filter values of one
 *              screen (CLAUDE.md §9d) — so there is no "Customer type" picker,
 *              the audience is fixed by the prop, it is always sent to the API,
 *              and it is NOT a URL key, so a shared link cannot cross the
 *              boundary either.
 *
 *              Insurance sold WITH a flight ticket IS listed here, tagged
 *              "With flight" and filterable by `origin`. It is still an ancillary
 *              of that booking, so its documents live on the FLIGHT — its row
 *              links to the flight's invoice (which prints the insurance invoice
 *              as its own document) and e-ticket, not to this screen's drawer,
 *              whose id space is `insurance_bookings` and collides.
 *
 *              The stat cards count STANDALONE policies only, deliberately: an
 *              add-on premium is already flight revenue, and a card here is also
 *              a status filter for the table, so this is the one place the two
 *              legitimately describe different sets. The "Sold via" filter is how
 *              an admin reconciles a card against the rows.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-10-09 Claude — ?open=<id> opens that policy's details drawer (global search).
 *   - 2026-09-23 Claude — Document links carry the screen they were opened from
 *                          (lib/docOrigin), so a corporate booking's invoice /
 *                          e-ticket keeps the Corporate group open in the
 *                          sidebar and its Back button returns to THIS list.
 *   - 2026-09-22 Claude — List policies sold WITH a flight alongside the
 *     standalone ones (client decision). They were invisible in the console
 *     entirely, so a policy the customer holds — and rings support about — could
 *     not be found. Added the "Sold via" filter, the origin tag, the region /
 *     duration detail those rows carry, and per-origin row actions.
 * *   - 2026-09-25 Claude — Share by Email, as the Flight and Hotel lists have had
 *     since 2026-08-04. A standalone policy shares its Tax Invoice and/or the
 *     insurer's policy document; an add-on row opens the FLIGHT's share, because
 *     those documents are the flight's.
 *   - 2026-09-25 Claude — Each row says where the INSURER's policy document has
 *     got to, and can be filtered by it. Only the separate Policy Requests queue
 *     knew before, so "has this customer got their policy?" — the question
 *     support opens THIS screen to answer — required knowing to look somewhere
 *     else. The chip links straight to the file when there is one.
 *   - 2026-09-22 Claude — Initial. Travel insurance was missing from the admin
 *     portal entirely — no list, no details, no invoice.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import BrandSelect from '../../components/BrandSelect';
import ShareEmailModal from '../../components/ShareEmailModal';
import DatePicker from '../../components/DatePicker';
import RowMenu from '../../components/RowMenu';
import { get } from '../../api/client';
import { docPath } from '../../lib/docOrigin';
import { money, day, dateTime, statusTone, journeyLabel, customerCell, docAccess } from './cells';
import BookingDetailsDrawer from './BookingDetailsDrawer';

// Four cards, not five: `insurance_bookings.booking_status` has no
// 'Cancellation Requested' member, so this product has no pending-cancellation
// bucket (a cancellation in flight shows through refund_status on the row).
const CARDS = [
  { key: 'success', label: 'Success', tone: 'card-ok' },
  { key: 'failed', label: 'Failed', tone: 'card-rej' },
  { key: 'pending', label: 'Pending', tone: 'card-pend' },
  { key: 'cancelled', label: 'Cancelled', tone: 'card-rej' },
];

const JOURNEYS = [
  { value: '', label: 'All plans' },
  { value: 'intl', label: 'International' },
  { value: 'student', label: 'Student' },
  { value: 'amt', label: 'Annual Multi Trip' },
];

// Which checkout sold the policy. NOT a separate screen: the audience split is
// what earns separate screens (§9d); this is ONE product bought two ways, and an
// admin chasing a policy should not have to know which funnel the customer used.
const ORIGINS = [
  { value: '', label: 'All policies' },
  { value: 'standalone', label: 'Bought on its own' },
  { value: 'flight', label: 'Sold with a flight' },
];

// Filters kept in the URL — everything a shared link should reproduce.
// `audience` is deliberately absent: it is the SCREEN, not a filter (§9d).
const URL_KEYS = ['status', 'brand', 'journey', 'origin', 'policyDoc', 'bookingFrom', 'bookingTo', 'travelFrom', 'travelTo', 'traveller'];

// Where the INSURER's policy document has got to. 'none' is the normal state —
// most customers never ask — so it is an explicit choice, not the placeholder.
const POLICY_DOCS = [
  { value: '', label: 'Any' },
  { value: 'requested', label: 'Waiting on us' },
  { value: 'uploaded', label: 'Sent to the customer' },
  { value: 'rejected', label: 'Refused' },
  { value: 'none', label: 'Never asked for' },
];

export default function BookingList({
  title = 'Insurance List',
  subtitle = 'B2C Insurance · travel insurance policies',
  audience = 'b2c',
} = {}) {
  // The row being shared by email, and WHICH product's documents those are. An
  // add-on policy's documents belong to its FLIGHT (§24), so that row opens the
  // flight share — the same documents the row's own links already point at.
  const [shareRow, setShareRow] = useState(null);
  const [params, setParams] = useSearchParams();
  // ?open=<id> lifts that policy's drawer (the global search links here), seeded
  // once; closing it clears the param — same as the flight / hotel lists.
  const [detailsId, setDetailsId] = useState(() => {
    const v = Number(params.get('open'));
    return Number.isInteger(v) && v > 0 ? v : null;
  });
  // A new ?open= while this screen is already mounted (the global search) opens
  // that booking too — the seed above only runs on the first render.
  const openParam = params.get('open');
  useEffect(() => {
    const v = Number(openParam);
    if (Number.isInteger(v) && v > 0) setDetailsId(v);
  }, [openParam]);

  const status = params.get('status') || null;
  const brand = params.get('brand') || '';
  const journey = params.get('journey') || '';
  const origin = params.get('origin') || '';
  const policyDoc = params.get('policyDoc') || '';
  const bookingFrom = params.get('bookingFrom') || '';
  const bookingTo = params.get('bookingTo') || '';
  const travelFrom = params.get('travelFrom') || '';
  const travelTo = params.get('travelTo') || '';
  const traveller = params.get('traveller') || '';

  /** Merge one or more filter changes into the URL in a single navigation. */
  function setFilters(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  // The stat cards belong to THIS screen's audience — a card is also a status
  // filter, so an unscoped count would advertise the other audience's volume
  // and then list a different number of rows when clicked.
  const { data: sum } = useQuery({
    queryKey: ['insurance-bookings', 'summary', audience],
    queryFn: () => get('/insurance/bookings/summary', { params: { audience } }),
  });
  const counts = sum?.data || {};

  const extraParams = { audience };
  for (const k of URL_KEYS) {
    const v = params.get(k) || '';
    if (v) extraParams[k] = v;
  }
  const anyFilterActive = URL_KEYS.some((k) => params.get(k));

  const toolbar = (
    <>
      <div className="stat-row">
        {CARDS.map((c) => (
          <button
            key={c.key}
            className={`stat-card ${c.tone}${status === c.key ? ' active' : ''}`}
            onClick={() => setFilters({ status: status === c.key ? '' : c.key })}
            title={`Filter ${c.label}`}
          >
            <span className="stat-num">{counts[c.key] ?? '—'}</span>
            <span className="stat-label">{c.label}</span>
          </button>
        ))}
        {anyFilterActive ? (
          <button className="btn danger" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <Icon name="x" /> Clear Filter
          </button>
        ) : null}
      </div>
      <div className="search-row" style={{ marginTop: 8 }}>
        <div className="field">
          <label>Brand</label>
          <BrandSelect value={brand} onChange={(v) => setFilters({ brand: v })} withAll />
        </div>
        <div className="field">
          <label>Plan type</label>
          <Select value={journey} onChange={(v) => setFilters({ journey: v })} options={JOURNEYS} />
        </div>
        <div className="field">
          <label>Sold via</label>
          <Select value={origin} onChange={(v) => setFilters({ origin: v })} options={ORIGINS} />
        </div>
        <div className="field">
          <label>Policy document</label>
          <Select value={policyDoc} onChange={(v) => setFilters({ policyDoc: v })} options={POLICY_DOCS} />
        </div>
      </div>
      {/* The cards count standalone policies only — an add-on premium is already
          counted as flight revenue, so adding it here would count the same money
          on two screens. Said out loud, because a card that does not match the
          rows beneath it is otherwise read as a bug (§9d). */}
      <p className="muted" style={{ margin: '6px 2px 0', fontSize: '.82em' }}>
        Cards count policies bought on their own. Policies sold with a flight are listed below
        and tagged <b>With flight</b> — their premium is already counted as flight revenue.
      </p>
      <div className="filter-row">
        <div className="field">
          <label>Booking date from</label>
          <DatePicker value={bookingFrom} onChange={(v) => setFilters({ bookingFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Booking date to</label>
          <DatePicker value={bookingTo} onChange={(v) => setFilters({ bookingTo: v })} min={bookingFrom} />
        </div>
        <div className="field">
          <label>Cover starts from</label>
          <DatePicker value={travelFrom} onChange={(v) => setFilters({ travelFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Cover starts to</label>
          <DatePicker value={travelTo} onChange={(v) => setFilters({ travelTo: v })} min={travelFrom} />
        </div>
        <div className="field grow">
          <label>Traveller name / contact</label>
          <input
            value={traveller}
            maxLength={120}
            onChange={(e) => setFilters({ traveller: e.target.value })}
            placeholder="Search by traveller name, email, phone…"
          />
        </div>
      </div>
    </>
  );

  // Details is an admin tool and always available. The Tax Invoice follows the
  // one shared status rule (lib/docAccess): nothing for pending or failed,
  // invoice for confirmed and for cancelled/refunded. There is no voucher or
  // "e-ticket" equivalent — the customer's travel document for this product is
  // the insurer's POLICY PDF, which is a separate piece of work.
  //
  // A policy SOLD WITH A FLIGHT is served by the flight's own documents, and its
  // `id` is a `flight_bookings.id` — handing it to this screen's drawer or
  // invoice route would open a DIFFERENT policy, because the two tables are
  // independent AUTO_INCREMENTs (the same collision migration 054 exists to
  // avoid). Its invoice route already prints the insurance invoice as a separate
  // document with its own download button.
  const rowActions = (row) => {
    // An add-on's documents belong to its FLIGHT, so they are gated on the
    // flight's status — a Failed premium (charged, no policy) on a ticketed
    // flight still has an invoice and an e-ticket, and that is the row support
    // most needs to open.
    const docs = docAccess(row.origin === 'flight' ? row.flight_status : row.booking_status);
    if (row.origin === 'flight') {
      return (
        <RowMenu
          label="Actions"
          items={[
            {
              key: 'flight-invoice',
              label: 'Flight + Insurance Invoice',
              icon: 'download',
              hidden: !docs.invoice,
              to: docPath(`/flight/bookings/${row.id}/invoice`, audience),
            },
            {
              key: 'ticket',
              label: 'E-Ticket',
              icon: 'page',
              hidden: !docs.invoice,
              to: docPath(`/flight/bookings/${row.id}/ticket`, audience),
            },
            {
              // The FLIGHT's share, because these are the flight's documents —
              // its invoice already prints the insurance invoice as its own
              // sub-document (§24). `row.id` on an add-on row IS
              // flight_bookings.id, which is what that endpoint expects.
              key: 'share',
              label: 'Share by Email',
              icon: 'mail',
              hidden: !docs.share,
              onClick: () => setShareRow({ row, kind: 'flight' }),
            },
          ]}
        />
      );
    }
    return (
      <RowMenu
        label="Actions"
        items={[
          { key: 'details', label: 'Details', icon: 'page', onClick: () => setDetailsId(row.id) },
          {
            key: 'invoice',
            label: 'Tax Invoice',
            icon: 'download',
            hidden: !docs.invoice,
            to: docPath(`/insurance/bookings/${row.id}/invoice`, audience),
          },
          {
            // Tax Invoice and/or the INSURER's policy document. There is no
            // certificate of ours to send — that was withdrawn on 2026-09-25
            // (§28) — so the second document is the real one an admin uploaded.
            key: 'share',
            label: 'Share by Email',
            icon: 'mail',
            hidden: !docs.share,
            onClick: () => setShareRow({ row, kind: 'insurance' }),
          },
        ]}
      />
    );
  };

  return (
    <>
      <ReadOnlyListPage
        title={title}
        subtitle={subtitle}
        endpoint="/insurance/bookings"
        queryRoot="insurance-bookings"
        searchLabel="Booking Ref / Policy No."
        actionsLabel="Action"
        rowActions={rowActions}
        toolbar={toolbar}
        extraParams={extraParams}
        showIndex={false}
        dense
        columns={[
          {
            key: 'booking_reference',
            label: 'Booking',
            render: (v, r) => (
              <div>
                <div className="mono"><b>{v || '—'}</b></div>
                <div className="muted" style={{ fontSize: '.8em' }} title={`Trace ID ${r.id}`}>
                  #{r.id} · {dateTime(r.created_at)}
                </div>
                {/* Which checkout sold it. On an add-on the reference above is the
                    FLIGHT's, so saying so is what stops it reading as a policy
                    reference that cannot be found in insurance_bookings. */}
                {r.origin === 'flight' ? (
                  <Chip tone="pend">With flight{r.pnr_code ? ` · ${r.pnr_code}` : ''}</Chip>
                ) : null}
              </div>
            ),
          },
          {
            key: 'journey',
            label: 'Plan',
            render: (v, r) => (
              <div>
                <div>{journeyLabel(v)}</div>
                <div className="muted" style={{ fontSize: '.8em' }}>
                  {[r.plan_tier, r.plan_cover].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            ),
          },
          {
            key: 'start_date',
            label: 'Cover',
            render: (v, r) => (
              <div>
                <div>{day(v)}</div>
                <div className="muted" style={{ fontSize: '.8em' }}>
                  to {day(r.end_date)}{r.duration_days ? ` · ${r.duration_days} days` : ''}
                </div>
              </div>
            ),
          },
          {
            key: 'destination',
            label: 'Destination',
            // The embedded journey prices a REGION, not a country list, so an
            // add-on row has no destination to state and shows its region
            // instead. A row from before the region was persisted (2026-09-22)
            // shows neither rather than a re-derived guess at what was
            // underwritten.
            render: (v, r) => (
              <div>
                <div>{v || r.region || '—'}</div>
                <div className="muted" style={{ fontSize: '.8em' }}>
                  {r.travellers ? `${r.travellers} traveller${Number(r.travellers) === 1 ? '' : 's'}` : '—'}
                </div>
              </div>
            ),
          },
          {
            key: 'policy_id',
            label: 'Policy No.',
            // A confirmed policy with no number is worth seeing as such, not as
            // an empty cell — it means the insurer never issued one.
            render: (v, r) => (v
              ? <span className="mono">{v}</span>
              : <span className="muted">{docAccess(r.booking_status).confirmed ? 'Not issued' : '—'}</span>),
          },
          { key: 'booking_status', label: 'Status', render: (v) => (v ? <Chip tone={statusTone(v)}>{v}</Chip> : '—') },
          {
            key: 'policy_doc_status',
            label: 'Policy doc',
            // Where the INSURER's document is. "Not requested" is stated rather
            // than left blank: an empty cell reads as missing data, and "nobody
            // asked" is the answer support needs most often.
            render: (v, r) => {
              if (v === 'uploaded') {
                return r.policy_doc_url
                  ? (
                    <a href={r.policy_doc_url} target="_blank" rel="noopener noreferrer" title="Open the document sent to the customer">
                      <Chip tone="ok">Sent</Chip>
                    </a>
                  )
                  : <Chip tone="ok">Sent</Chip>;
              }
              if (v === 'requested') {
                return (
                  <Chip tone="pend">
                    Waiting{Number(r.policy_doc_requests) > 1 ? ` · ${r.policy_doc_requests}×` : ''}
                  </Chip>
                );
              }
              if (v === 'rejected') return <Chip tone="rej">Refused</Chip>;
              return <span className="muted">Not requested</span>;
            },
          },
          { key: 'customer_name', label: 'Customer', render: customerCell },
          { key: 'total_paid', label: 'Amount', align: 'right', render: (v) => <b>{money(v)}</b> },
        ]}
      />

      {shareRow ? (
        <ShareEmailModal
          kind={shareRow.kind}
          booking={shareRow.row}
          onClose={() => setShareRow(null)}
        />
      ) : null}

      {detailsId ? (
        <BookingDetailsDrawer bookingId={detailsId} audience={audience} onClose={() => { setDetailsId(null); if (params.get('open')) setFilters({ open: '' }); }} />
      ) : null}
    </>
  );
}
